// Sales & Purchases — CLEARED FOR REDESIGN.
//
// The old list-screen implementation (tab strips, filters, saved views, peek slide-over, bulk
// bar, keyboard layer, seven-family grid) has been removed so the Sales / Purchases / Bills
// UX can be redesigned from scratch. `documentsListScreen` now renders a single placeholder
// panel that keeps the nav working while the redesign is in flight — no data is shown.
//
// Cross-file callers (main.js, dashboard.js, contacts.js, approvals.js, globalsearch.js) still
// import the domain constants + status helpers below, so their own screens continue to render
// document badges and links unchanged. The heavy list/detail/editor is coming back — this file
// deliberately kept minimal until the new design lands.
import { html, signal, navigate } from '../shared/t4.js';
import { tenantShell } from './tshell.js';
import { t } from '../shared/i18n.js';
import { api } from '../shared/api.js';
import { notice as toast } from '../shared/notice.js';
import { fmtMoney } from './bank_helpers.js';


// ── Domain constants (still consumed by other screens) ───────────────────────────────────────
// Every document family, keyed by its /co/{id}/{screen} segment. The two ALL entries are
// aggregate views that widen the list to every type on that side (Sales vs Purchases).
const PURCHASE_TYPES = ['PORD', 'GRV', 'RTS', 'SINV'];
export const DOC_TYPES = {
  'all-sales':        { type: 'ALL',  key: 'all-sales',        group: 'sales',
                        t: 'nav.all_sales_v2', label: 'All',           noun: 'document',
                        multiTypes: ['QUO', 'INV', 'CRN'] },
  'all-purchases':    { type: 'ALL',  key: 'all-purchases',    group: 'purchase',
                        t: 'nav.all_purchases_v2', label: 'All',       noun: 'document',
                        multiTypes: PURCHASE_TYPES },
  'invoices':         { type: 'INV',  key: 'invoices',         group: 'sales',
                        t: 'nav.invoices', label: 'Invoices',           noun: 'Invoice' },
  'quotes':           { type: 'QUO',  key: 'quotes',           group: 'sales',
                        t: 'nav.quotes', label: 'Quotes',               noun: 'Quote' },
  'credit-notes':     { type: 'CRN',  key: 'credit-notes',     group: 'sales',
                        t: 'nav.credit_notes', label: 'Credit Notes',   noun: 'Credit Note' },
  'bills':            { type: 'SINV', key: 'bills',            group: 'purchase',
                        t: 'nav.bills', label: 'Supplier Invoices',     noun: 'Supplier Invoice' },
  'purchase-orders':  { type: 'PORD', key: 'purchase-orders',  group: 'purchase',
                        t: 'nav.purchase_orders', label: 'Purchase Orders', noun: 'Purchase Order' },
  'goods-received':   { type: 'GRV',  key: 'goods-received',   group: 'purchase',
                        t: 'nav.goods_received', label: 'Goods Received', noun: 'Goods Receipt' },
  'supplier-credits': { type: 'RTS',  key: 'supplier-credits', group: 'purchase',
                        t: 'nav.supplier_credit_notes', label: 'Supplier Credit Notes',
                        noun: 'Supplier Credit Note', nounKey: 'doc.supplier_credit_note' },
};


// ── Status pill helpers (still consumed by dashboard / contacts / globalsearch) ──────────────
const STATUS_LABEL = {
  all: ['common.all', 'All'], draft: ['status.draft', 'Draft'], issued: ['status.issued', 'Issued'],
  posted: ['status.due', 'Due'], partial: ['status.partially_paid', 'Partially paid'],
  overdue: ['status.overdue', 'Overdue'], paid: ['status.paid', 'Paid'],
  void: ['status.void', 'Void'], cancelled: ['status.cancelled', 'Cancelled'],
};
const STATUS_HELP = {
  draft: ['status.help_draft', 'Not issued yet — the customer has never seen it.'],
  issued: ['status.help_issued', 'Finalised and numbered, but not yet sent to the customer.'],
  posted: ['status.help_posted', 'Posted and awaiting payment.'],
  overdue: ['status.help_overdue', 'Past its due date and still unpaid.'],
  partial: ['status.help_partial', 'Part-paid — some of the balance is still outstanding.'],
  paid: ['status.help_paid', 'Settled in full — nothing outstanding.'],
  void: ['status.help_void', 'Cancelled — the posting was reversed. Kept for the record.'],
  cancelled: ['status.help_cancelled', 'Cancelled — nothing more is expected on this document.'],
};
const PILL_MAP = { posted: 'due', issued: 'open' };

export function statusLabel(s){ const p = STATUS_LABEL[s] || [null, s]; return t(p[0], p[1]); }
export function statusClass(s){ const k = s || 'draft'; return 'pill pill-' + (PILL_MAP[k] || k); }
export function statusHelp(s){ const p = STATUS_HELP[s] || [null, '']; return t(p[0], p[1]); }
export function isDraftDocNo(n){ return typeof n === 'string' && n.startsWith('DRAFT'); }
export function documentTypeLabel(doc){
  return doc && doc.type_code === 'RTS'
    ? t('doc.supplier_credit_note', 'Supplier Credit Note')
    : ((doc && doc.type_name) || '');
}


// type_code → route segment (docKey), for opening a row's document.
const KEY_FOR_TYPE = Object.entries(DOC_TYPES).reduce((m, [k, v]) => {
  if(v.type && v.type !== 'ALL') m[v.type] = k;
  return m;
}, {});
export const SALES_TABS = [['all-sales', 'All'], ['quotes', 'Quotes'], ['invoices', 'Invoices'], ['credit-notes', 'Credit Notes'], ['sales-memos', 'Memos']];
const PURCHASE_TABS = [['all-purchases', 'All'], ['purchase-orders', 'Purchase Orders'],
  ['goods-received', 'Goods Received Voucher'], ['supplier-credits', 'Supplier Credit Notes', 'nav.supplier_credit_notes'],
  ['bills', 'Supplier Invoices', 'nav.supplier_invoices']];
const SALES_TYPES = ['QUO', 'INV', 'CRN'];
const DOCUMENTS_PER_PAGE = 200;  // API clamps this endpoint to 200; consume its full paginated envelope.
// Review 2026-10-19: loading "every page" had no ceiling, and the API itself stops at 20,000 rows per
// query (documents_service.list_documents) while still reporting that as the total. Each document type
// is loaded on its own (?type=) up to this many rows; past it the list says it is incomplete.
const MAX_ROWS_PER_TYPE = 5000;
const API_ROW_CAP = 20000;
// Money documents: the All tab's total adds only these (an order and its goods-received voucher are
// the same purchase as its supplier invoice, so adding them too counted one purchase up to three times).
const FINANCIAL_TYPES = ['INV', 'CRN', 'SINV', 'RTS'];
const isVoidDocument = d => ['void', 'cancelled'].includes(String(d.status || '').toLowerCase());
const dmy = iso => { if(!iso) return ''; const p = String(iso).slice(0, 10).split('-'); return p.length === 3 ? `${p[2]}/${p[1]}/${p[0]}` : iso; };

// Sales & Purchases list — search + family tabs (All / Quotes / Invoices / Credit Notes on Sales;
// All / Purchase Orders / GRVs / Supplier Credit Notes on Purchases), matching the contacts landing layout (owner
// Andre 2026-09-18). No Add button — new documents start from a contact's New ▾ dropdown.
export function documentsListScreen(companyId, docKey){
  const cfg = DOC_TYPES[docKey] || DOC_TYPES['all-sales'];
  const group = cfg.group === 'purchase' ? 'purchase' : 'sales';
  const family = group === 'purchase' ? t('nav.purchases_v2', 'Purchases') : t('nav.sales_v2', 'Sales');
  const tabs = group === 'purchase' ? PURCHASE_TABS : SALES_TABS;
  const familyTypes = group === 'purchase' ? PURCHASE_TYPES : SALES_TYPES;
  const activeTypes = (docKey === 'all-sales' || docKey === 'all-purchases')
    ? familyTypes
    : (DOC_TYPES[docKey] && DOC_TYPES[docKey].type ? [DOC_TYPES[docKey].type] : familyTypes);

  const rows = signal([]), currencies = signal(new Map()), baseCurrency = signal(null),
        loading = signal(true), err = signal(''), q = signal(''), newOpen = signal(false),
        incomplete = signal([]);   // [{type, shown, total, capped}] for types not loaded in full
  const draftOnly = signal(false);        // quick "Drafts" filter pill (status === draft), alongside the family tabs
  // "+ New" dropdown items for this family (owner Andre 2026-09-18: add a New dropdown like contacts).
  const NEW_ITEMS = group === 'purchase'
    ? [['purchase-orders', 'Purchase Order'], ['goods-received', 'Goods Received Voucher'],
       ['supplier-credits', 'Supplier Credit Note', 'doc.supplier_credit_note']]
    : [['quotes', 'Quote'], ['invoices', 'Invoice'], ['credit-notes', 'Credit Note']];
  const cbase = '/companies/' + companyId;
  function currencyForDocument(d){
    const hasId = d.currency_id !== null && d.currency_id !== undefined && d.currency_id !== '';
    const known = hasId ? (currencies.value.get(String(d.currency_id)) || null) : baseCurrency.value;
    // The row's own currency when the API sends it — a currency since switched off is no longer in
    // the reference list, and its figures must still carry its own mark (owner 2026-10-19).
    if(d.currency_code || d.currency_symbol){
      const k = known || {};
      return { id: hasId ? d.currency_id : k.id, code: d.currency_code || k.code || '',
               symbol: d.currency_symbol || k.symbol || '' };
    }
    return known;
  }
  function amountText(value, currency){
    const amount = Number(value || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    const unknown = t('doc.currency_unavailable', 'Currency unavailable');
    if(!currency) return unknown + ' ' + amount;
    const marker = currency.symbol || currency.code;
    if(!marker) return unknown + ' ' + amount;
    // the code only where it adds something: a document in a currency other than the company's own
    const base = baseCurrency.value;
    const foreign = !!currency.code && !(base && String(base.id) === String(currency.id));
    return fmtMoney(value, marker, true) + (foreign ? ' ' + currency.code : '');
  }
  function signedDocumentTotal(d){
    const total = Number(d.total || 0);
    return group === 'purchase' && d.type_code === 'RTS' ? -Math.abs(total) : total;
  }
  function amountForDocument(d){
    const currency = currencyForDocument(d);
    if(currency) return amountText(signedDocumentTotal(d), currency);
    return amountText(signedDocumentTotal(d), null);
  }
  // Each load gets a number; a newer load (filter, confirm, re-mount) or leaving the screen makes an
  // older one stop and drop its result instead of overwriting the newer list.
  let loadSeq = 0;
  const startPath = typeof location !== 'undefined' ? location.pathname : '';
  const stillWanted = seq => seq === loadSeq && (typeof location === 'undefined' || location.pathname === startPath);
  async function loadType(type, seq){
    // One document type, page by page, until its total, the per-type ceiling, or an empty page.
    // Rows that appear twice (a document confirmed or added while paging shifts the offsets) are
    // skipped rather than failing the whole list.
    const out = [], seen = new Set();
    let total = 0;
    for(let page = 1; ; page++){
      const params = new URLSearchParams({ page: String(page), per_page: String(DOCUMENTS_PER_PAGE), type });
      const res = await api.get(cbase + '/documents?' + params.toString());
      if(!stillWanted(seq)) return null;
      const data = Array.isArray(res && res.data) ? res.data : null;
      if(!data) throw new Error('The documents API returned an invalid page');
      if(page === 1) total = Number.isSafeInteger(res.total) && res.total >= 0 ? res.total : data.length;
      for(const row of data){
        if(row.id === null || row.id === undefined || seen.has(String(row.id))) continue;
        seen.add(String(row.id)); out.push(row);
      }
      const pages = Number.isSafeInteger(res.total_pages) ? res.total_pages : page;
      if(!data.length || page >= pages || out.length >= total || out.length >= MAX_ROWS_PER_TYPE) break;
    }
    const capped = total >= API_ROW_CAP;            // the API stopped counting: there may be more
    return { rows: out.slice(0, MAX_ROWS_PER_TYPE), total, capped };
  }
  async function load(){
    const seq = ++loadSeq;
    loading.value = true;
    try {
      const reference = await api.get(cbase + '/documents-reference');
      if(!stillWanted(seq)) return;
      const currencyRows = Array.isArray(reference.currencies) ? reference.currencies : [];
      const currencyMap = new Map(currencyRows.filter(c => c && c.id !== undefined && c.id !== null)
        .map(c => [String(c.id), c]));
      const baseId = reference.base_currency_id;
      const base = (baseId !== null && baseId !== undefined ? currencyMap.get(String(baseId)) : null) ||
        (reference.currency_symbol || reference.currency_code ? {
          id: baseId, code: reference.currency_code || '', symbol: reference.currency_symbol || '', is_base: true,
        } : null);
      if(base && base.id !== null && base.id !== undefined) currencyMap.set(String(base.id), base);
      const allRows = [], short = [];
      for(const type of activeTypes){                // only the types this tab shows
        const got = await loadType(type, seq);
        if(!got) return;                             // superseded or the user left the screen
        allRows.push(...got.rows);
        if(got.capped || got.rows.length < got.total)
          short.push({ type, shown: got.rows.length, total: got.total, capped: got.capped });
      }
      currencies.value = currencyMap;
      baseCurrency.value = base;
      rows.value = allRows;
      incomplete.value = short;
      err.value = '';
    } catch(e){
      if(!stillWanted(seq)) return;
      rows.value = []; incomplete.value = [];
      err.value = (e.data && e.data.error) || t('doc.load_failed', 'Could not load documents');
    } finally {
      if(seq === loadSeq) loading.value = false;
    }
  }
  load();

  // ── Row actions (inline, right-aligned) ──────────────────────────────────────────────────────
  // CONFIRM posts a draft (existing endpoint); CLONE copies any document to a same-type draft and
  // opens it; REPEAT clones an invoice/order re-dated to today. Two clicks, never leave the list.
  const busy = signal(0);   // id currently acting on, so its buttons can disable
  const REPEAT_TYPES = ['INV', 'SINV', 'QUO', 'PORD'];
  const openEditor = (typeCode, id) => navigate('/co/' + companyId + '/' + (KEY_FOR_TYPE[typeCode] || docKey) + '/' + id + '/edit');
  async function confirmRow(d){
    if(busy.value) return; busy.value = d.id;
    try { await api.post(cbase + '/documents/' + d.id + '/confirm', {}); toast(t('doc.confirmed', 'Confirmed')); await load(); }
    catch(e){ toast((e.data && e.data.error) || t('doc.confirm_failed', 'Could not confirm'), 'warn'); }
    finally { busy.value = 0; }
  }
  async function cloneRow(d, redate){
    if(busy.value) return; busy.value = d.id;
    try {
      const res = await api.post(cbase + '/documents/' + d.id + '/clone', { redate: !!redate });
      toast(redate ? t('doc.repeated', 'Repeated — new draft opened') : t('doc.cloned', 'Cloned — new draft opened'));
      openEditor(res.type_code || d.type_code, res.id);
    } catch(e){ toast((e.data && e.data.error) || t('doc.clone_failed', 'Could not clone'), 'warn'); busy.value = 0; }
  }

  const list = () => {
    if(loading.value) return html`<div class="muted-block">${t('common.loading', 'Loading…')}</div>`;
    if(err.value) return html`<div class="muted-block">${err.value}</div>`;
    const needle = q.value.trim().toLowerCase();
    const drafts = draftOnly.value;         // read so the list re-filters when the pill toggles
    const body = rows.value
      .filter(d => activeTypes.includes(d.type_code))
      .filter(d => !drafts || d.status === 'draft')
      .filter(d => !needle || [d.document_no, d.contact_name, d.reference]
        .some(v => v && String(v).toLowerCase().includes(needle)));
    if(!body.length){
      return html`<div class="empty muted-block">${needle
        ? t('doc.none_match', 'No documents match') + ' “' + q.value.trim() + '”.'
        : t('doc.none_yet', 'No documents yet — start one from a contact.')}</div>`;
    }
    const allTab = docKey === 'all-sales' || docKey === 'all-purchases';
    const counted = body.filter(d => !isVoidDocument(d) && (!allTab || FINANCIAL_TYPES.includes(d.type_code)));
    const sums = new Map();
    for(const d of counted){
      const currency = currencyForDocument(d);
      const id = currency && currency.id !== undefined && currency.id !== null
        ? String(currency.id) : (currency && currency.code ? currency.code : 'unknown:' + String(d.currency_id ?? ''));
      const entry = sums.get(id) || { currency, total: 0 };
      entry.total += signedDocumentTotal(d);
      sums.set(id, entry);
    }
    const sumText = [...sums.values()].map(({ currency, total }) => amountText(total, currency)).join(' · ');
    const short = incomplete.value;
    const shortNote = short.length ? html`<div class="muted-block doc-list-incomplete" data-doc-incomplete>${
      short.map(x => t('doc.list_incomplete', 'Only {shown} of {total} {type} documents are listed. Search to narrow the list.')
        .replace('{shown}', x.shown.toLocaleString('en-US'))
        .replace('{total}', x.total.toLocaleString('en-US') + (x.capped ? '+' : ''))
        .replace('{type}', x.type)).join(' ')}</div>` : '';
    return html`${shortNote}<div class="tbl-wrap"><table class="list doc-list">
      <thead><tr><th>${t('doc.col_number', 'Number')}</th><th>${t('common.date', 'Date')}</th>
        <th>${group === 'purchase' ? t('contacts.supplier', 'Supplier') : t('contacts.customer', 'Customer')}</th>
        <th>${t('common.status', 'Status')}</th><th class="ta-r">${t('doc.col_total', 'Total')}</th>
        <th class="ta-r doc-act-col">${t('common.actions', 'Actions')}</th></tr></thead>
      <tbody>${body.map(d => html`<tr class="clickable"
          @click=${() => openEditor(d.type_code, d.id)}>
        <td><span class="mono">${isDraftDocNo(d.document_no) || !d.document_no ? t('status.draft', 'Draft') : d.document_no}</span>
          <small class="doc-type-sub">${documentTypeLabel(d)}</small></td>
        <td class="mono">${dmy(d.document_date)}</td>
        <td>${d.contact_name || ''}</td>
        <td><span class=${statusClass(d.status)}>${statusLabel(d.status)}</span></td>
        <td class="mono ta-r">${amountForDocument(d)}</td>
        <td class="ta-r doc-row-actions" @click=${e => e.stopPropagation()}>
          ${d.status === 'draft' ? html`<button type="button" class="btn btn-sm btn-primary"
            ?disabled=${() => busy.value === d.id}
            @click=${() => confirmRow(d)}>${t('doc.confirm_post', 'Confirm')}</button>` : ''}
          <button type="button" class="btn btn-sm btn-outline"
            ?disabled=${() => busy.value === d.id}
            @click=${() => cloneRow(d, false)}>${t('doc.clone', 'Clone')}</button>
          ${REPEAT_TYPES.includes(d.type_code) ? html`<button type="button" class="btn btn-sm btn-outline"
            ?disabled=${() => busy.value === d.id}
            @click=${() => cloneRow(d, true)}>${t('doc.repeat', 'Repeat')}</button>` : ''}
        </td></tr>`)}</tbody>
      <tfoot><tr class="doc-list-total"><td colspan="4" class="ta-r">${t('doc.total', 'Total')}</td>
        <td class="mono ta-r">${sumText}</td><td></td></tr></tfoot>
    </table></div>`;
  };

  const content = html`<div class="page page-wide ct-page">
    <div class="page-head"><h1>${family}</h1></div>
    <div class="doc-toolbar">
      <div class="ct-new-dd">
        <button type="button" class="btn btn-sm btn-primary ct-new-btn"
          @click=${() => { newOpen.value = !newOpen.value; }}>+ ${t('common.new', 'New')} <span class="ct-new-caret">▾</span></button>
        ${() => newOpen.value ? html`
          <div class="ct-new-backdrop" @click=${() => { newOpen.value = false; }}></div>
          <div class="ct-new-menu">${NEW_ITEMS.map(([seg, label, labelKey]) => html`<button type="button" class="ct-new-item"
            @click=${() => { newOpen.value = false; navigate('/co/' + companyId + '/' + seg + '/new'); }}>${labelKey ? t(labelKey, label) : label}</button>`)}</div>` : ''}
      </div>
      <input class="form-control search ux-search" .value=${q} @input=${e => { q.value = e.target.value; }}
        placeholder=${t('doc.search_ph', 'Search number, contact, reference…')}>
      <div class="segs">
        <button type="button" class=${'seg' + (tabs[0][0] === docKey ? ' on' : '')}
          @click=${() => navigate('/co/' + companyId + '/' + tabs[0][0])}>${tabs[0][1]}</button>
        <button type="button" class=${() => 'seg seg-draft' + (draftOnly.value ? ' on' : '')}
          @click=${() => { draftOnly.value = !draftOnly.value; }}>${t('doc.drafts', 'Drafts')}</button>
        ${tabs.slice(1).map(([k, label, labelKey]) => html`<button type="button"
          class=${'seg' + (k === docKey ? ' on' : '')}
          @click=${() => navigate('/co/' + companyId + '/' + k)}>${labelKey ? t(labelKey, label) : label}</button>`)}
      </div>
    </div>
    ${list}
  </div>`;
  return tenantShell(companyId, family, content);
}
